/**
 * JoinOtpStep — OTP entry on the /join/<token> page.
 *
 * Single responsibility: verify the OTP and report back. The parent
 * (JoinPage) decides what comes next — that's either an
 * account-completion step (when the user just signed in for the first
 * time and has no name+email yet) or going straight to acceptInvite.
 *
 * Splitting the concerns this way keeps the OTP component reusable
 * and the orchestrator (JoinPage) the single place that knows the
 * whole state machine.
 *
 * The OTP field auto-submits at six digits, with a `lastAttemptedCode`
 * guard against the retry loop documented in `onboarding/otp-form.tsx`.
 */
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Form,
  MetaPill,
  OtpField,
  Submit,
  SubmitProbe,
} from "@flint/ui";
import { identity } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../lib/trpc.ts";
import { meQueryKey } from "../../lib/session.ts";

type FormValues = z.infer<typeof identity.verifyOtpInput>;

export interface JoinOtpStepProps {
  challengeId: string;
  /** Display-only; the user already entered this on the previous step. */
  phone: string;
  /** "Use a different phone number" — bubbled to parent. */
  onChangePhone: () => void;
  /**
   * Called after `verifyOtp` succeeds AND `me` has been refetched. The
   * parent uses this to decide whether to render the account-completion
   * step or call acceptInvite directly.
   */
  onVerified: () => void;
}

export function JoinOtpStep({ challengeId, phone, onChangePhone, onVerified }: JoinOtpStepProps) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(identity.verifyOtpInput),
    defaultValues: { challengeId, code: "" },
  });

  // Keep the form's challengeId aligned if the parent issues a new OTP.
  useEffect(() => {
    form.setValue("challengeId", challengeId);
  }, [challengeId, form]);

  const verifyOtp = trpc.identity.verifyOtp.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    if (!values.challengeId) return;
    await verifyOtp.mutateAsync(values);
    // Force a refetch (not just invalidate). The parent's branch logic
    // reads `me.user.profileComplete`; it must see the post-verify
    // user row before the render. Mirrors the pattern in
    // `onboarding/otp-form.tsx`.
    await queryClient.refetchQueries({ queryKey: meQueryKey });
    onVerified();
  });

  // Auto-submit when six digits arrive. Same retry-loop guard as the
  // onboarding OTP form: only fire when the entered code differs from
  // the last one we attempted.
  const code = form.watch("code");
  const lastAttempted = useRef<string | null>(null);
  useEffect(() => {
    if (
      code?.length === 6 &&
      code !== lastAttempted.current &&
      !form.formState.isSubmitting &&
      !verifyOtp.isPending &&
      challengeId
    ) {
      lastAttempted.current = code;
      void onSubmit();
    }
  }, [code, form.formState.isSubmitting, verifyOtp.isPending, challengeId, onSubmit]);

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-4">
              <div>
                <p className="text-sm text-ink">
                  Code sent to <span className="font-mono tabular-nums">{maskFull(phone)}</span>
                </p>
                <p className="text-xs text-muted mt-0.5">
                  Check WhatsApp first — we&apos;ll fall back to SMS if delivery fails.
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <MetaPill>Single-use code</MetaPill>
                <MetaPill>5-minute expiry</MetaPill>
              </div>

              <OtpField
                name="code"
                length={6}
                helper="Paste works. Slots advance as each digit arrives."
                autoSubmitOnComplete
              />

              <Submit pendingLabel="Verifying" disabled={verifyOtp.isPending}>
                Verify and continue
              </Submit>

              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={onChangePhone}
                disabled={verifyOtp.isPending}
              >
                Use a different phone number
              </Button>

              {verifyOtp.error ? (
                <Alert
                  intent="bad"
                  title="Couldn't verify code"
                  description={verifyOtp.error.message}
                />
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}

/** `9876543210` → `+91 98xxxx XX10`. Same masking shape the server uses. */
function maskFull(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (d.length !== 10) return phone;
  return `+91 ${d.slice(0, 2)}xxxx XX${d.slice(-2)}`;
}
